import io from 'socket.io-client';

// Dynamically determine Socket URL based on current domain
const getSocketUrl = () => {
  const currentDomain = window.location.hostname;
  
  // Check for runtime config first (loaded from config.js)
  const runtimeConfig = window.ENV_CONFIG || {};
  
  if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
    // Development environment
    return runtimeConfig.REACT_APP_SOCKET_URL || process.env.REACT_APP_SOCKET_URL || 'http://localhost:8080';
  } else if (currentDomain === 'admin.jaddid.ae') {
    // Production environment
    return runtimeConfig.REACT_APP_PROD_SOCKET_URL || process.env.REACT_APP_PROD_SOCKET_URL || 'https://api.jaddid.ae';
  } else if (currentDomain.includes('run.app')) {
    // Cloud Run environment (for testing before custom domain mapping)
    return runtimeConfig.REACT_APP_CLOUDRUN_SOCKET_URL || process.env.REACT_APP_CLOUDRUN_SOCKET_URL || 'https://jaddid-backend-123876256131.me-central1.run.app';
  } else {
    // Fallback
    return runtimeConfig.REACT_APP_SOCKET_URL || process.env.REACT_APP_SOCKET_URL || 'http://localhost:8080';
  }
};

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000;
  }

  // Initialize socket connection
  connect(token) {
    if (this.socket && this.connected) {
      return Promise.resolve();
    }

    // Fallback: grab token from localStorage if not passed explicitly
    const effectiveToken = token || localStorage.getItem('adminToken');

    return new Promise((resolve, reject) => {
      try {
        const socketURL = getSocketUrl();
        
        this.socket = io(socketURL, {
          transports: ['websocket'],
          timeout: 5000, // Add 5 second timeout
          auth: {
            token: effectiveToken,
            userType: 'admin'
          },
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectInterval,
          forceNew: true, // Force new connection
        });

        // Add connection timeout
        const connectionTimeout = setTimeout(() => {
          if (!this.connected) {
            console.error('Socket connection timeout');
            this.socket?.disconnect();
            reject(new Error('Socket connection timeout'));
          }
        }, 10000); // 10 second timeout

        this.socket.on('connect', () => {
          clearTimeout(connectionTimeout);
          this.connected = true;
          this.reconnectAttempts = 0;
          
          // Get admin info from localStorage for user identification
          const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}');
          const adminName = adminInfo.name || adminInfo.username || 'Support Agent';
          const adminId = adminInfo.id || 'admin';
          
          // Join admin support room automatically with proper identification
          this.socket.emit('join_support_session', {
            sessionId: 'admin_support',
            userType: 'admin',
            userId: adminId,
            senderName: adminName
          });
          
          resolve();
        });

        this.socket.on('disconnect', (reason) => {
          this.connected = false;
        });

        this.socket.on('connect_error', (error) => {
          console.error('Admin socket connection error:', error);
          this.connected = false;
          clearTimeout(connectionTimeout);
          reject(error);
        });

        this.socket.on('reconnect', (attemptNumber) => {
          this.connected = true;
          this.reconnectAttempts = 0;
          
          // Rejoin admin support room after reconnection
          this.socket.emit('join_admin_support');
        });

        this.socket.on('reconnect_error', (error) => {
          console.error('Admin socket reconnection error:', error);
          this.reconnectAttempts++;
        });

        this.socket.on('reconnect_failed', () => {
          console.error('Admin socket reconnection failed after max attempts');
          this.connected = false;
          reject(new Error('Socket reconnection failed'));
        });

      } catch (error) {
        console.error('Failed to initialize admin socket:', error);
        reject(error);
      }
    });
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  // Support-specific methods
  joinSupportSession(sessionId) {
    if (this.socket && this.connected) {
      // Get admin info from localStorage for user identification
      const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}');
      const adminName = adminInfo.name || adminInfo.username || 'Support Agent';
      const adminId = adminInfo.id || 'admin';
      
      this.socket.emit('join_support_session', { 
        sessionId,
        userType: 'admin',
        userId: adminId,
        senderName: adminName
      });
    }
  }

  sendSupportMessage(sessionId, message) {
    if (this.socket && this.connected) {
      // Get admin info from localStorage for user identification
      const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}');
      const adminName = adminInfo.name || adminInfo.username || 'Support Agent';
      const adminId = adminInfo.id || 'admin';
      
      this.socket.emit('support_message', {
        sessionId,
        message,
        userType: 'admin',
        userId: adminId,
        senderName: adminName
      });
    }
  }

  requestLiveAgent(sessionId) {
    if (this.socket && this.connected) {
      this.socket.emit('request_live_agent', { sessionId });
    }
  }

  endSupportSession(sessionId) {
    if (this.socket && this.connected) {
      this.socket.emit('end_support_session', { sessionId });
    }
  }

  startTyping(sessionId) {
    if (this.socket && this.connected) {
      this.socket.emit('typing_start', { sessionId });
    }
  }

  stopTyping(sessionId) {
    if (this.socket && this.connected) {
      this.socket.emit('typing_stop', { sessionId });
    }
  }

  // Event listeners
  onSupportMessage(callback) {
    if (this.socket) {
      this.socket.on('support_message_received', callback);
    }
  }

  onNewSupportSession(callback) {
    if (this.socket) {
      // Backend emits 'new_agent_request' (with a 'new_support_session'
      // legacy alias on some flows). Subscribe to both so we never miss
      // a new conversation regardless of which path created it.
      this.socket.on('new_support_session', callback);
      this.socket.on('new_agent_request', callback);
    }
  }

  onAgentRequest(callback) {
    if (this.socket) {
      // Backend emits 'agent_requested' (past-tense). Keep the older
      // 'agent_request' alias too for safety.
      this.socket.on('agent_request', callback);
      this.socket.on('agent_requested', callback);
    }
  }

  // New: dedicated alert event the backend now fires when a user/vendor
  // sends a support message. Used by the admin panel to play a ping and
  // raise a browser desktop notification.
  onAdminSupportAlert(callback) {
    if (this.socket) {
      this.socket.on('admin_support_alert', callback);
    }
  }

  offAdminSupportAlert() {
    if (this.socket) {
      this.socket.off('admin_support_alert');
    }
  }

  onSessionEnded(callback) {
    if (this.socket) {
      this.socket.on('support_session_ended', callback);
    }
  }

  onTypingIndicator(callback) {
    if (this.socket) {
      this.socket.on('typing_indicator', callback);
    }
  }

  onSupportNotification(callback) {
    if (this.socket) {
      this.socket.on('support_notification', callback);
    }
  }

  onUnreadCountUpdated(callback) {
    if (this.socket) {
      this.socket.on('unread_count_updated', callback);
    }
  }

  offUnreadCountUpdated() {
    if (this.socket) {
      this.socket.off('unread_count_updated');
    }
  }

  // Remove event listeners
  offSupportMessage() {
    if (this.socket) {
      this.socket.off('support_message_received');
    }
  }

  offNewSupportSession() {
    if (this.socket) {
      this.socket.off('new_support_session');
      this.socket.off('new_agent_request');
    }
  }

  offAgentRequest() {
    if (this.socket) {
      this.socket.off('agent_request');
      this.socket.off('agent_requested');
    }
  }

  offSessionEnded() {
    if (this.socket) {
      this.socket.off('support_session_ended');
    }
  }

  offTypingIndicator() {
    if (this.socket) {
      this.socket.off('typing_indicator');
    }
  }

  offSupportNotification() {
    if (this.socket) {
      this.socket.off('support_notification');
    }
  }

  offUnreadCountUpdated() {
    if (this.socket) {
      this.socket.off('unread_count_updated');
    }
  }

  // Check connection status
  isConnected() {
    return this.connected && this.socket && this.socket.connected;
  }
}

// Export singleton instance
export default new SocketService();